/**
 * Which listings may be offered in an "open for inspection" property picker.
 *
 * Paul Jimoulis, 28 Sep 2026, CRM-2026-00079 (Monday 13146667884): "When you
 * select the property to put out a open time it is showing all properties
 * including withdrawns. Please only show On Market and Off Market property
 * listings. Withdrawns are no longer for sale with us so they should not show
 * when looking to put out inspection times."
 *
 * The real fix is server-side — the pickers now ask for
 * `active_listings_only=true` (see property_listing_views.py), so the 500-row
 * page they request is already filtered and `count` stays honest. This module
 * is the matching client-side guard, and it exists for two reasons that are not
 * paranoia:
 *
 *  1. `apiCache` holds the property list for 10 minutes and survives a reload,
 *     so a browser that fetched before the fix still has the unfiltered rows.
 *  2. The pickers are also fed from callers that pass `properties` straight in
 *     (the property detail Open Homes tab), which never went through the
 *     filtered request.
 *
 * It replaces an earlier guard that read `p.portal_category !== 'sold'`. That
 * test never did anything: `portal_category` is the Buyer Portal *type* tab
 * (house, land, commercial, …) and has no 'sold' member, so the only condition
 * actually holding sold listings back was the `is_sold` flag beside it.
 */

/** Statuses a booking dropdown may offer, matched loosely — see the BE note. */
const LIVE_LISTING_STATUSES = ['on market', 'off market'];

/** The status name as a lowercase string, whether it arrives as a string or FK object. */
const statusNameOf = (property) => {
  const raw = property?.status;
  const name = raw && typeof raw === 'object' ? raw.name : raw;
  return String(name ?? '').trim().toLowerCase();
};

/**
 * True when an open home can still be scheduled against this property.
 *
 * Withdrawn, sold, leased, appraisal-stage and not-currently-listed properties
 * are all out; only a live On Market / Off Market listing is in.
 */
export const isSchedulableListing = (property) => {
  if (!property) return false;
  if (property.is_withdrawn) return false;
  // A leased rental is recorded through the sale plumbing, so is_sold covers
  // both "Mark as Sold" and "Mark as Leased".
  if (property.is_sold) return false;

  const status = statusNameOf(property);
  if (status.includes('sold') || status.includes('leased')) return false;
  return LIVE_LISTING_STATUSES.some((live) => status.includes(live));
};

/**
 * The subset of `properties` an inspection picker may offer, order preserved.
 *
 * `keepId` is the property already chosen on the form. It survives the filter
 * even when it is no longer schedulable, because these pickers are also the
 * EDIT form: an open home booked before its listing was withdrawn still has to
 * open on the property it belongs to. Dropping it would leave the `required`
 * <select> showing a blank option, so re-saving a time on an existing session
 * would either be blocked or silently re-point it at another address.
 */
export const schedulableProperties = (properties, keepId = null) => {
  const kept = keepId == null || keepId === '' ? null : String(keepId);
  return (properties || []).filter(
    (p) => isSchedulableListing(p) || (kept !== null && String(p?.id) === kept)
  );
};

export default schedulableProperties;
